import { useCallback, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { useStartLiveEdit } from '@/features/instruments/api'
import { instrumentInfoEditPath } from '@/features/instruments/lib/instrumentNavigation'
import { useToast } from '@/shared/toast/ToastProvider'
import { LiveEditConfirmDialog } from '../components/LiveEditConfirmDialog'
import { messageFromUnknownApiError } from '../lib/mapApiUnknownError'

/** Minimal row shape shared by launch rows and quick (direct) instrument cards. */
export type LiveEditTarget = Readonly<{ id: string; status: string }>

/** BFP-1142 — Live / Paused surveys can be edited and republished. */
export function isLiveEditableInstrument(instrument: Pick<LiveEditTarget, 'status'>): boolean {
  return instrument.status === 'active' || instrument.status === 'paused'
}

/** Pencil → confirm → open pending revision → Update Instrument (info step). */
export function useLiveEditEntry(): {
  requestLiveEdit: (instrument: LiveEditTarget) => void
  liveEditDialog: ReactNode
} {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const startLiveEdit = useStartLiveEdit()
  const [target, setTarget] = useState<LiveEditTarget | null>(null)

  const requestLiveEdit = useCallback((instrument: LiveEditTarget) => {
    if (!isLiveEditableInstrument(instrument)) return
    setTarget(instrument)
  }, [])

  const close = useCallback(() => {
    if (!startLiveEdit.isPending) setTarget(null)
  }, [startLiveEdit.isPending])

  const confirm = useCallback(() => {
    if (!target) return
    const instrumentId = target.id
    startLiveEdit.mutate(instrumentId, {
      onSuccess: () => {
        setTarget(null)
        navigate(instrumentInfoEditPath(instrumentId))
      },
      onError: (err) => {
        showToast(messageFromUnknownApiError(err, 'Could not start editing this survey'), 'error')
      },
    })
  }, [navigate, showToast, startLiveEdit, target])

  const liveEditDialog = (
    <LiveEditConfirmDialog
      open={target != null}
      confirming={startLiveEdit.isPending}
      onClose={close}
      onConfirm={confirm}
    />
  )

  return { requestLiveEdit, liveEditDialog }
}
